import theme from "@configs/theme";
import { cacheKeys } from "@constants/cache-keys";
import { useAuthContext } from "@features/auth/contexts/AuthContext";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import { getAuthToken } from "@helpers/auth-provider";
import { formatNumberWithAbbreviation } from "@memorang/helpers/common";
import type { Stat } from "@memorang/ui/components/RowStats";
import { useQuery } from "@tanstack/react-query";
import pluralize from "pluralize";
import { fetchInsights } from "../api/queries";
import type { TagDetail } from "../api/schema";

type Params = {
	itemId?: number;
	trigger?: boolean;
};

const calculateStrengthStats = (tagDetails: TagDetail[] | undefined | null) => {
	if (!tagDetails?.length) {
		return [];
	}

	const stats: Stat[] = [];

	return stats;
};

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
export const useInsights = (params?: Params) => {
	const { trigger = true } = params ?? {};
	const { userPublicMetadataId } = useAuthContext();
	const {
		currentExamContext: { studyPackCollectionId, projectId, organizationId },
	} = useExamContext();
	const { data: insightsData, isLoading } = useQuery({
		queryKey: [cacheKeys.insights],
		queryFn: async () => {
			const token = await getAuthToken();

			if (!token) {
				throw new Error("No token");
			}

			return await fetchInsights({
				collectionId: studyPackCollectionId!,
				projectId,
				organizationId,
			});
		},
		enabled: trigger && !!userPublicMetadataId,
	});

	const showEmptyState = !(insightsData?.data.challengeCompleted || isLoading);

	const overallProgressData = {
		title: "Key stats",
		subtitle: "Based on your activity so far",
		insights: "You'are a super star in Cardiology!",
		tooltip: "",
		stats: [
			{
				label: "Completed",
				value: formatNumberWithAbbreviation(
					insightsData?.data.challengeCompleted ?? 0,
				),
				icon: "arm-flex-outline",
				color: theme().palette.success.dark,
			},
			{
				label: "Accuracy",
				value: Number.isNaN(Number(insightsData?.data.accuracy))
					? "0%"
					: `${Math.round(Number(insightsData?.data.accuracy) * 100)}%`,
				icon: "target",
				color: theme().palette.yellow.light,
			},
		],
	};

	const overallAchievementsData = {
		title: "Your achievements",
		subtitle: "Based on your activity so far",
		insights: "You'are a super star in Cardiology!",
		tooltip: "",
		rank: insightsData?.data.achivements?.level ?? 0,
		xp: insightsData?.data.achivements?.currentXP ?? 0,
		xpToNextLevel: insightsData?.data.achivements?.remainingXP ?? 0,
		stats: [
			{
				label: "Rank",
				value: `Level ${insightsData?.data.achivements?.level ?? 0}`,
				icon: "shield-star-outline",
			},
			{
				label: "Points earned",
				value: `${formatNumberWithAbbreviation(insightsData?.data.achivements?.currentXP ?? 0)} XP`,
				icon: "star-circle-outline",
			},
			{
				label: "To next rank",
				value: `${formatNumberWithAbbreviation(insightsData?.data.achivements?.remainingXP ?? 0)} XP`,
				icon: "ladder",
			},
		],
	};

	const calculateAvgGuessesPerChallenge = () => {
		const distribution = insightsData?.data.guessDistribution?.attempts ?? {
			"1": 0,
			"2": 0,
			"3": 0,
			"4": 0,
			"5": 0,
		};

		const totalAttempts = Object.keys(distribution).length;
		if (totalAttempts === 0) {
			return 0;
		}

		const weightedSum = Object.values(distribution).reduce((sum, count) => {
			return sum + count;
		}, 0);

		return weightedSum / totalAttempts;
	};

	const overallGuessDistributionData = {
		title: "Your guess distribution",
		subtitle: "Based on challenges completed",
		insights: "You'are a super star in Cardiology!",
		tooltip: "",
		numChallengesCompleted: insightsData?.data.challengeCompleted ?? 0,
		distribution: insightsData?.data.guessDistribution?.attempts ?? {
			"1": 0,
			"2": 0,
			"3": 0,
			"4": 0,
			"5": 0,
		},
		stats: [
			{
				label: "Challenges",
				value: formatNumberWithAbbreviation(
					insightsData?.data.challengeCompleted ?? 0,
				),
				icon: "check-circle-outline",
			},
			{
				label: "AVG Guesses",
				value: formatNumberWithAbbreviation(calculateAvgGuessesPerChallenge()),
				icon: "comment-question-outline",
			},
		],
	};

	const overallRankData = {
		title: "Your rank",
		subtitle: "Based on your activity so far",
		insights: "You'are a super star in Cardiology!",
		tooltip: "",
	};

	const overallPerformanceData = {
		title: "Peformance by system",
		subtitle: "Your accuracy on each speciality",
		insights: "You'are a super star in Cardiology!",
		tooltip: "",
		specialties: [
			{ name: "Cardiology", progress: 50 },
			{ name: "Dermatology", progress: 75 },
			{ name: "Neurology", progress: 25 },
			{ name: "Oncology", progress: 60 },
			{ name: "Pediatrics", progress: 80 },
			{ name: "Psychiatry", progress: 40 },
			{ name: "Urology", progress: 90 },
		],
	};

	const overallStreakData = {
		title: "Your streak",
		subtitle: "Ongoing stream you have accumulated",
		insights: "You'are a super star in Cardiology!",
		tooltip: "",
		stats: [
			{
				label: "Current",
				value: `${pluralize("day", insightsData?.data.streaks?.currentStreak ?? 0, true)}`,
				icon: "fire",
				color: theme().palette.yellow.main,
			},
			{
				label: "Best",
				value: `${pluralize(
					"day",
					insightsData?.data.streaks?.bestStreak ?? 0,
					true,
				)}`,
				icon: "calendar-check-outline",
				color: theme().palette.yellow.dark,
			},
		],
	};

	const strengthsAndWeaknessData =
		insightsData?.data.tagDetails?.length &&
		insightsData?.data.tagDetails?.length > 0
			? {
					title: "Strengths and challenges",
					subtitle: `Based on the ${insightsData.data.tagDetails.length} specialties you've encountered`,
					insights: "You're a super star in Cardiology!",
					tooltip: "",
					stats: calculateStrengthStats(insightsData.data.tagDetails),
				}
			: undefined;

	return {
		overallProgressData,
		overallRankData,
		overallAchievementsData,
		overallGuessDistributionData,
		overallPerformanceData,
		overallStreakData,
		strengthsAndWeaknessData,
		isLoading,
		showEmptyState,
	};
};
